import React, { FC } from 'react';
import CloseButton from '@westwing/ui-kit/CloseButton';
import AndroidIcon from '@westwing/ui-kit/Icons/AndroidIcon';
import AppleIcon from '@westwing/ui-kit/Icons/AppleIcon';
import TagIcon from '@westwing/ui-kit/Icons/TagIcon';
import CalendarIcon from '@westwing/ui-kit/Icons/Svgs/CalendarIcon';
import RoomPlanner from '@westwing/ui-kit/Icons/RoomPlanner';
import ReferAFriend from '@westwing/ui-kit/Icons/ReferAFriend';
import GiftIcon from '@westwing/ui-kit/Icons/GiftIcon';
import Inspiration from '@westwing/ui-kit/Icons/Svgs/Inspiration';
import WW2 from '@westwing/ui-kit/Icons/Svgs/WW2';
import useDeviceType from '../hooks/useDeviceType';
import {
    Clickable,
    BorderedBox,
    IconContainer,
    Title,
    Section,
    SectionTitle,
    ContentOverlay,
    CloseIconContainer,
    Sections,
} from './styled/BurgerMenu';
import { BurgerMenuContentProps, BurgerMenuItemProps } from './types/BurgerMenu';
import { SECTIONS } from './helpers/BurgerMenuSections';
import { useHeaderContext } from 'Components/HeaderContext';
import { HeaderContent, LineBreaker, LogoWrapper, TabsWrapper } from 'Components/Header/HeaderElements';
import { Logo } from 'Components/Logo';
import { Tabs } from 'Components/Tabs';

const ICON_BY_ITEM_KEYS = {
    android: AndroidIcon,
    ios: AppleIcon,
    cop: TagIcon,
    cup: CalendarIcon,
    wws: RoomPlanner,
    raf: ReferAFriend,
    gift: GiftIcon,
    inspiration: Inspiration,
    shop: WW2,
};

export const BurgerMenuItem: FC<BurgerMenuItemProps> = ({
    title,
    link,
    linkTitle,
    label,
    dataNoOverlay,
    id,
    dataTestId,
    tel,
    onClick,
}: BurgerMenuItemProps): JSX.Element => {
    const { t } = useHeaderContext();
    const Icon = label && ICON_BY_ITEM_KEYS[label];
    const href = tel ? `tel:${t(link)}` : link;

    return (
        <Clickable
            id={id}
            href={href}
            title={linkTitle && t(linkTitle)}
            data-no-overlay={dataNoOverlay}
            data-testid={dataTestId}
            onClick={onClick}
        >
            <BorderedBox>
                {Icon && (
                    <IconContainer>
                        {Icon === Inspiration ? <Icon height={48} width={48} /> : <Icon height={20} width={20} />}
                    </IconContainer>
                )}
                <Title>{t(title)}</Title>
            </BorderedBox>
        </Clickable>
    );
};

export const BurgerMenuContent: FC<BurgerMenuContentProps> = ({
    isLoggedIn,
    isBurgerMenuMyWWStyleItemEnabled,
    voucherMenuUrl,
    smartAppBannerUrl,
    onClose,
    firstRowProps,
    country,
}: BurgerMenuContentProps): JSX.Element => {
    const { t } = useHeaderContext();
    const { getByUserAgent } = useDeviceType();
    const device = getByUserAgent();
    const {
        trackingHelpers: {
            ga: { trackNonInteractiveGenericEvent },
            useCurrentPageType,
        },
    } = useHeaderContext();
    const { pageType } = useCurrentPageType();

    const handleClick = (action: string) => {
        trackNonInteractiveGenericEvent('Navigation', { action: `${action} - Click`, label: `${pageType}` });
    };

    const sectionList = SECTIONS.map((section, sectionIndex) => {
        if (section.onlyForLoggedInUsers && !isLoggedIn) {
            return null;
        }

        // eslint-disable-next-line complexity
        const itemList = section.items.map(item => {
            if (item.onlyForLoggedInUsers && !isLoggedIn) {
                return null;
            }

            if (item.label === 'inspiration' && !isBurgerMenuMyWWStyleItemEnabled) {
                return null;
            }

            if (item.label === 'wws' && !['DE', 'CH', 'NL'].includes(country)) {
                return null;
            }

            let itemLink = item.link;

            if (item.label === 'gift') {
                if (!voucherMenuUrl) {
                    return null;
                }
                itemLink = voucherMenuUrl;
            }

            let itemLabel = item.label;
            let itemTrackingAction = item.title;

            if (item.label === 'app') {
                if (!smartAppBannerUrl || device === 'UNKNOWN') {
                    return null;
                }
                itemLink = smartAppBannerUrl;
                itemLabel = device.toLowerCase();
                itemTrackingAction = `${itemTrackingAction} - ${itemLabel}`;
            }

            return (
                <BurgerMenuItem
                    key={`item-${item.title}`}
                    title={item.title}
                    link={itemLink}
                    linkTitle={item.linkTitle}
                    label={itemLabel}
                    dataNoOverlay={item.dataNoOverlay}
                    id={item.id}
                    dataTestId={item.dataTestId}
                    tel={item.tel}
                    onClick={() => handleClick(itemTrackingAction)}
                />
            );
        });

        return (
            <Section key={`section-${section.title}`} data-testid={section.dataTestId}>
                <SectionTitle $isMainSectionTitle={sectionIndex === 0}>{t(section.title)}</SectionTitle>
                {itemList}
            </Section>
        );
    });

    return (
        <ContentOverlay>
            <HeaderContent>
                <LogoWrapper>
                    <Logo href={firstRowProps.logoHref} linkComponent={firstRowProps.logoConfigLinkComponent} />
                </LogoWrapper>
                <TabsWrapper>
                    <Tabs
                        navItems={firstRowProps.tabsConfig}
                        onboardingTooltipConfig={firstRowProps.onboardingTooltipConfig}
                        isStickyHeaderActive={firstRowProps.isStickyHeaderActive}
                    />
                </TabsWrapper>
            </HeaderContent>

            <LineBreaker />

            <CloseIconContainer onClick={onClose} data-testid="oww-header-burger-menu-close">
                <CloseButton size="13px" />
            </CloseIconContainer>
            <Sections>{sectionList}</Sections>
        </ContentOverlay>
    );
};
